import { ONE_BIPS } from '../../../../constants'
import { JSBI, Percent, type Pair, type TokenAmount } from '../../../../sdk'
import { useMemo } from 'react'
import { useTotalSupply } from '../../../../data/TotalSupply'
import { useTokenBalance } from '../../../../state/wallet/hooks'

export function formatSharePercent(percent?: Percent, { emptyAsDash = false }: { emptyAsDash?: boolean } = {}) {
  if (!percent || JSBI.equal(percent.numerator, JSBI.BigInt(0))) return emptyAsDash ? '—' : '0%'
  if (percent.lessThan(ONE_BIPS)) return '<0.01%'
  return `${percent.toFixed(2)}%`
}

export function useProjectedPoolShare(
  pair: Pair | null | undefined,
  liquidityMinted: TokenAmount | undefined,
  account?: string | null,
) {
  const userPoolBalance = useTokenBalance(account ?? undefined, pair?.liquidityToken)
  const totalSupply = useTotalSupply(pair?.liquidityToken)

  return useMemo(() => {
    if (!liquidityMinted || !totalSupply) return undefined
    const userRaw = userPoolBalance?.raw ?? JSBI.BigInt(0)
    const newUser = JSBI.add(userRaw, liquidityMinted.raw)
    const newTotal = JSBI.add(totalSupply.raw, liquidityMinted.raw)
    if (JSBI.equal(newTotal, JSBI.BigInt(0))) return undefined
    return new Percent(newUser, newTotal)
  }, [liquidityMinted, totalSupply, userPoolBalance])
}

export function useProjectedPoolShareAfterRemove(
  pair: Pair | null | undefined,
  liquidityToRemove: TokenAmount | undefined,
  account?: string | null,
) {
  const userPoolBalance = useTokenBalance(account ?? undefined, pair?.liquidityToken)
  const totalSupply = useTotalSupply(pair?.liquidityToken)

  return useMemo(() => {
    if (!liquidityToRemove || !totalSupply || !userPoolBalance) return undefined
    const remainingUser = JSBI.subtract(userPoolBalance.raw, liquidityToRemove.raw)
    if (JSBI.lessThan(remainingUser, JSBI.BigInt(0))) return undefined
    const newTotal = JSBI.subtract(totalSupply.raw, liquidityToRemove.raw)
    if (JSBI.equal(newTotal, JSBI.BigInt(0)) || JSBI.equal(remainingUser, JSBI.BigInt(0))) return undefined
    return new Percent(remainingUser, newTotal)
  }, [liquidityToRemove, totalSupply, userPoolBalance])
}
